/**
 * The Netly Pay overview — the Memberships tab of the dashboard.
 *
 * Four stat cards that each link to the screen explaining them, eight weeks of
 * collections, today's sessions, the activity feed and the quick actions.
 * Before the shop is linked this renders the Enable screen instead, because
 * `not_linked` is a state and not an error (D54).
 *
 * Every figure comes from `overview.ts`, which is tested; this file lays them
 * out. Spec: DESIGN-BRIEF.md §B and `vOverview()` in prototype.html.
 */
import React from 'react';
import {useNavigate} from 'react-router-dom';
import {connect} from 'react-redux';
import NetlyPayRepository from '../../../internals/repository/NetlyPayRepository';
import {fmtDateTime, money, todayISO} from '../../../internals/managers/NetlyPayManager';
import {activityGlyph, overviewStats, todaySessions, weekBars} from '../overview';
import {useNetlyPayFetch, useNetlyPayLink} from '../hooks/useNetlyPay';
import {ErrorState, Skeleton} from '../components/States';
import EnableNetlyPayView from './EnableNetlyPayView';

const nowHHMM = () => {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

const Card = ({children, className = ''}: {children: React.ReactNode; className?: string}) => (
  <div className={`bg-surface border border-borderc rounded-card ${className}`}>{children}</div>
);

const SectionTitle = ({children, aside}: {children: React.ReactNode; aside?: React.ReactNode}) => (
  <div className="flex items-baseline justify-between gap-4 px-5 pt-4 pb-3">
    <h3 className="text-[15px] font-semibold text-text1">{children}</h3>
    {aside}
  </div>
);

const toneClass: Record<string, string> = {
  default: 'text-text1',
  danger: 'text-neg',
  accent: 'text-brand',
};

export const NetlyPayOverview = ({selectedShop}: any) => {
  const navigate = useNavigate();
  const shopId = selectedShop?.shopId;
  const currency = selectedShop?.currency ? `${selectedShop.currency}` : 'Rs.';
  const link = useNetlyPayLink(shopId);

  const overview = useNetlyPayFetch(
    link.phase === 'linked' ? shopId : null,
    (id) => NetlyPayRepository.overview(id),
    [link.phase],
  );

  if (link.phase === 'loading') {
    return (
      <Card>
        <Skeleton rows={4} />
      </Card>
    );
  }

  if (link.phase === 'error') {
    return (
      <Card>
        <ErrorState message={link.error} onRetry={link.reload} />
      </Card>
    );
  }

  if (link.phase === 'not_linked') {
    return (
      // Only an owner may create or attach a workspace (D53). That is
      // enforced by the API, which answers 403 `forbidden`; the wizard shows
      // that as "Only the shop owner can turn Netly Pay on." The dashboard
      // has no reliable owner flag on the current user, and inventing one
      // here would hide the button from people who are allowed to press it.
      <EnableNetlyPayView shopId={shopId} shop={selectedShop} onEnabled={link.reload} />
    );
  }

  if (overview.phase === 'loading') {
    return (
      <Card>
        <Skeleton rows={4} />
      </Card>
    );
  }

  if (overview.phase === 'error' || !overview.data) {
    return (
      <Card>
        <ErrorState message={overview.error} onRetry={overview.reload} />
      </Card>
    );
  }

  const payload = overview.data;
  const today = todayISO();
  const stats = overviewStats(payload, currency);
  const weeks = weekBars(payload.weeks, today, currency);
  const sessions = todaySessions(payload, today, nowHHMM());
  const activity = (payload.activity ?? [])
    .slice()
    .sort((a, b) => b.ts.localeCompare(a.ts))
    .slice(0, 7);
  const collectedOverWeeks = Object.values(payload.weeks ?? {}).reduce(
    (s: number, v: any) => s + (Number(v) || 0),
    0,
  );

  return (
    <div className="flex flex-col gap-4">
      {/* Stat cards */}
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {stats.map((s) => (
          <button
            key={s.label}
            type="button"
            onClick={() => navigate(s.to)}
            className="text-left bg-surface border border-borderc rounded-card p-5 hover:border-brand transition-colors"
          >
            <span className="block text-[11px] font-semibold tracking-[.06em] text-text3">
              {s.label}
            </span>
            <span
              className={`block font-mono font-semibold tabular-nums mt-2 ${toneClass[s.tone]}`}
              style={{fontSize: 'clamp(18px,1.6vw,22px)'}}
            >
              {s.value}
            </span>
            <span className="block text-[12px] text-text2 mt-1.5">{s.sub}</span>
            {s.barPct != null && (
              <span className="block mt-3 h-1.5 rounded-full bg-surface3 overflow-hidden">
                <span className="block h-full bg-brand rounded-full" style={{width: `${s.barPct}%`}} />
              </span>
            )}
          </button>
        ))}
      </div>

      <div className="grid gap-4 xl:grid-cols-[1.4fr_1fr]">
        {/* Collections chart */}
        <Card>
          <SectionTitle
            aside={
              <span className="text-[12px] text-text3">
                {money(collectedOverWeeks, currency)} over 8 weeks
              </span>
            }
          >
            Collections by week
          </SectionTitle>
          <div className="px-5 pb-5">
            <div className="flex items-end gap-2 h-[150px]">
              {weeks.map((b) => (
                <div key={b.week} className="flex-1 flex flex-col items-center justify-end h-full gap-2">
                  <span className="text-[10.5px] font-mono text-text3 tabular-nums">{b.value}</span>
                  <div
                    className="w-full rounded-t-[4px] min-h-[2px]"
                    style={{
                      height: `${b.heightPct}%`,
                      background: b.isCurrent
                        ? 'repeating-linear-gradient(135deg, var(--brand) 0 5px, var(--brand2) 5px 10px)'
                        : 'var(--brand)',
                    }}
                    title={`${b.label}: ${b.value}`}
                  />
                </div>
              ))}
            </div>
            <div className="flex gap-2 mt-2">
              {weeks.map((b) => (
                <span
                  key={b.week}
                  className={`flex-1 text-center text-[10.5px] ${
                    b.isCurrent ? 'text-brand font-semibold' : 'text-text3'
                  }`}
                >
                  {b.label}
                </span>
              ))}
            </div>
          </div>
        </Card>

        {/* Today's sessions */}
        <Card>
          <SectionTitle aside={<span className="text-[12px] text-text3">{sessions.length}</span>}>
            Today's sessions
          </SectionTitle>
          <div className="px-5 pb-5">
            {sessions.length === 0 ? (
              <p className="text-[13px] text-text3 py-6 text-center">
                No sessions scheduled for today.
              </p>
            ) : (
              <div className="flex flex-col divide-y divide-borderc">
                {sessions.map((s) => (
                  <div key={s.id} className="py-3 flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-[13px] font-semibold text-text1 truncate">{s.name}</p>
                      <p className="text-[11.5px] text-text3 mt-0.5 truncate">{s.where}</p>
                      <p className="text-[11.5px] font-mono text-text3 tabular-nums mt-0.5">
                        {s.time} · {s.expected}
                      </p>
                    </div>
                    <span
                      className={`text-[11px] font-semibold px-2 py-1 rounded-md whitespace-nowrap ${
                        s.state === 'In progress' ? 'bg-pos-soft text-pos' : 'bg-surface3 text-text2'
                      }`}
                    >
                      {s.state}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Card>
      </div>

      <div className="grid gap-4 xl:grid-cols-[1.4fr_1fr]">
        {/* Activity */}
        <Card>
          <SectionTitle>Recent activity</SectionTitle>
          <div className="px-5 pb-5">
            {activity.length === 0 ? (
              <p className="text-[13px] text-text3 py-6 text-center">Nothing has happened yet.</p>
            ) : (
              <div className="flex flex-col divide-y divide-borderc">
                {activity.map((a, i) => {
                  const g = activityGlyph(a.kind);
                  return (
                    <div key={`${a.ts}-${i}`} className="py-3 flex items-start gap-3">
                      <span
                        className={`w-6 h-6 rounded-lg flex items-center justify-center text-[12px] font-semibold flex-shrink-0 ${g.classes}`}
                      >
                        {g.glyph}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-[13px] text-text1">{a.text}</p>
                        <p className="text-[11.5px] font-mono text-text3 tabular-nums mt-0.5">
                          {fmtDateTime(a.ts)}
                        </p>
                      </div>
                      {a.amountMinor != null && (
                        <span className="text-[12.5px] font-mono tabular-nums text-text2 flex-shrink-0">
                          {money(a.amountMinor, currency)}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </Card>

        {/* Quick actions */}
        <Card>
          <SectionTitle>Quick actions</SectionTitle>
          <div className="px-5 pb-5 flex flex-col gap-2.5">
            {[
              {
                title: 'New request',
                text: 'Bill a member, a batch or a program.',
                to: '/payment-requests',
                accent: true,
              },
              {title: 'Members', text: 'Search, add and edit the roster.', to: '/members'},
              {
                title: 'Approve payments',
                text: `${payload.waiting?.count ?? 0} proofs waiting · ${money(
                  payload.waiting?.amountMinor ?? 0,
                  currency,
                )}`,
                to: '/approvals',
              },
            ].map((q) => (
              <button
                key={q.title}
                type="button"
                onClick={() => navigate(q.to)}
                className={`text-left p-3.5 rounded-xl border transition-colors ${
                  q.accent
                    ? 'border-brand-soft bg-brand-soft/40 hover:border-brand'
                    : 'border-borderc hover:bg-surface2'
                }`}
              >
                <span
                  className={`block text-[13px] font-semibold ${q.accent ? 'text-brand' : 'text-text1'}`}
                >
                  {q.title}
                </span>
                <span className="block text-[12px] text-text2 mt-0.5">{q.text}</span>
              </button>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default connect((state: any) => ({
  selectedShop: state.dashboard.get('selectedShop'),
}))(NetlyPayOverview);
